/**
 * FAST Application Theme System
 *
 * Four themes optimized for warehouse management environments:
 * - Dark: Night shifts, low-light warehouses
 * - Light: Office environments, well-lit areas
 * - High Contrast: Accessibility, outdoor loading docks, bright sunlight
 * - Industrial: Warehouse floor, safety-conscious design
 *
 * Green (#22c55e) is used subtly across all themes for:
 * - Success states
 * - Confirmations
 * - Active/selected indicators
 */

// Shared colors used across all themes
export const SHARED_COLORS = {
  // Success/confirmation - subtle green
  success: {
    main: '#22c55e',
    light: '#4ade80',
    dark: '#16a34a',
    subtle: 'rgba(34, 197, 94, 0.1)',
    subtleHover: 'rgba(34, 197, 94, 0.2)',
  },
  // Error/danger
  error: {
    main: '#ef4444',
    light: '#f87171',
    dark: '#dc2626',
    subtle: 'rgba(239, 68, 68, 0.1)',
  },
  // Warning/caution
  warning: {
    main: '#f59e0b',
    light: '#fbbf24',
    dark: '#d97706',
    subtle: 'rgba(245, 158, 11, 0.1)',
  },
  // Info
  info: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
    subtle: 'rgba(59, 130, 246, 0.1)',
  },
};

// Theme definitions
export const THEMES = {
  dark: {
    id: 'dark',
    name: 'Dark',
    description: 'Night shifts, low-light warehouses',
    icon: 'DarkMode',

    colors: {
      // Backgrounds — slightly lifted from pure black for eye comfort
      background: '#0f0f12',
      surface: '#1a1a1f',
      surfaceElevated: '#2a2a30',
      surfaceHover: '#3a3a42',

      // Primary accent - blue for dark theme
      primary: {
        main: '#3b82f6',
        light: '#60a5fa',
        dark: '#2563eb',
        contrastText: '#ffffff',
      },

      // Secondary accent
      secondary: {
        main: '#8b5cf6',
        light: '#a78bfa',
        dark: '#7c3aed',
      },

      // Text — brightened for readability on dark backgrounds
      text: {
        primary: '#f4f4f5',
        secondary: '#d4d4d8',
        muted: '#a1a1aa',
        disabled: '#71717a',
      },

      // Borders — slightly brighter for visibility
      border: {
        default: '#3f3f46',
        light: '#52525b',
        focus: '#3b82f6',
      },

      // Interactive states
      hover: {
        background: 'rgba(255, 255, 255, 0.07)',
        text: '#ffffff',
      },

      // Message bubbles
      message: {
        user: '#3f3f46',
        bot: '#27272a',
      },

      // Shared semantic colors
      ...SHARED_COLORS,
    },

    // Shadows
    shadows: {
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
    },
  },

  light: {
    id: 'light',
    name: 'Light',
    description: 'Office environments, well-lit areas',
    icon: 'LightMode',

    colors: {
      // Backgrounds
      background: '#fafafa',
      surface: '#ffffff',
      surfaceElevated: '#f4f4f5',
      surfaceHover: '#e4e4e7',

      // Primary accent - blue for light theme
      primary: {
        main: '#2563eb',
        light: '#3b82f6',
        dark: '#1d4ed8',
        contrastText: '#ffffff',
      },

      // Secondary accent
      secondary: {
        main: '#7c3aed',
        light: '#8b5cf6',
        dark: '#6d28d9',
      },

      // Text
      text: {
        primary: '#18181b',
        secondary: '#52525b',
        muted: '#71717a',
        disabled: '#a1a1aa',
      },

      // Borders
      border: {
        default: '#e4e4e7',
        light: '#d4d4d8',
        focus: '#2563eb',
      },

      // Interactive states
      hover: {
        background: 'rgba(0, 0, 0, 0.04)',
        text: '#18181b',
      },

      // Message bubbles
      message: {
        user: '#e4e4e7',
        bot: '#f4f4f5',
      },

      // Shared semantic colors
      ...SHARED_COLORS,
    },

    // Shadows
    shadows: {
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    },
  },

  highContrast: {
    id: 'highContrast',
    name: 'High Contrast',
    description: 'Accessibility, outdoor use, bright sunlight',
    icon: 'Contrast',

    colors: {
      // Backgrounds - pure black for maximum contrast
      background: '#000000',
      surface: '#0a0a0a',
      surfaceElevated: '#171717',
      surfaceHover: '#262626',

      // Primary accent - bright yellow for visibility
      primary: {
        main: '#facc15',
        light: '#fde047',
        dark: '#eab308',
        contrastText: '#000000',
      },

      // Secondary accent - cyan for distinction
      secondary: {
        main: '#22d3ee',
        light: '#67e8f9',
        dark: '#06b6d4',
      },

      // Text - high contrast white
      text: {
        primary: '#ffffff',
        secondary: '#e5e5e5',
        muted: '#d4d4d4',
        disabled: '#737373',
      },

      // Borders - visible borders
      border: {
        default: '#404040',
        light: '#525252',
        focus: '#facc15',
      },

      // Interactive states
      hover: {
        background: 'rgba(255, 255, 255, 0.1)',
        text: '#ffffff',
      },

      // Message bubbles
      message: {
        user: '#262626',
        bot: '#171717',
      },

      // Semantic colors - brighter for visibility
      success: {
        main: '#4ade80',
        light: '#86efac',
        dark: '#22c55e',
        subtle: 'rgba(74, 222, 128, 0.15)',
        subtleHover: 'rgba(74, 222, 128, 0.25)',
      },
      error: {
        main: '#f87171',
        light: '#fca5a5',
        dark: '#ef4444',
        subtle: 'rgba(248, 113, 113, 0.15)',
      },
      warning: {
        main: '#fbbf24',
        light: '#fcd34d',
        dark: '#f59e0b',
        subtle: 'rgba(251, 191, 36, 0.15)',
      },
      info: {
        main: '#60a5fa',
        light: '#93c5fd',
        dark: '#3b82f6',
        subtle: 'rgba(96, 165, 250, 0.15)',
      },
    },

    // Shadows - minimal for high contrast
    shadows: {
      sm: '0 1px 2px 0 rgba(255, 255, 255, 0.1)',
      md: '0 4px 6px -1px rgba(255, 255, 255, 0.1)',
      lg: '0 10px 15px -3px rgba(255, 255, 255, 0.1)',
    },
  },

  industrial: {
    id: 'industrial',
    name: 'Industrial',
    description: 'Warehouse floor, safety-conscious design',
    icon: 'Factory',

    colors: {
      // Backgrounds - slate tones
      background: '#1e293b',
      surface: '#334155',
      surfaceElevated: '#475569',
      surfaceHover: '#64748b',

      // Primary accent - safety orange
      primary: {
        main: '#f97316',
        light: '#fb923c',
        dark: '#ea580c',
        contrastText: '#000000',
      },

      // Secondary accent - steel blue
      secondary: {
        main: '#0ea5e9',
        light: '#38bdf8',
        dark: '#0284c7',
      },

      // Text
      text: {
        primary: '#f8fafc',
        secondary: '#cbd5e1',
        muted: '#94a3b8',
        disabled: '#64748b',
      },

      // Borders
      border: {
        default: '#475569',
        light: '#64748b',
        focus: '#f97316',
      },

      // Interactive states
      hover: {
        background: 'rgba(255, 255, 255, 0.08)',
        text: '#f8fafc',
      },

      // Message bubbles
      message: {
        user: '#475569',
        bot: '#334155',
      },

      // Semantic colors - safety-inspired
      success: {
        main: '#22c55e',
        light: '#4ade80',
        dark: '#16a34a',
        subtle: 'rgba(34, 197, 94, 0.15)',
        subtleHover: 'rgba(34, 197, 94, 0.25)',
      },
      error: {
        main: '#ef4444',
        light: '#f87171',
        dark: '#dc2626',
        subtle: 'rgba(239, 68, 68, 0.15)',
      },
      warning: {
        main: '#eab308', // Safety yellow
        light: '#facc15',
        dark: '#ca8a04',
        subtle: 'rgba(234, 179, 8, 0.15)',
      },
      info: {
        main: '#0ea5e9',
        light: '#38bdf8',
        dark: '#0284c7',
        subtle: 'rgba(14, 165, 233, 0.15)',
      },
    },

    // Shadows
    shadows: {
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.2)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.4)',
    },
  },
};

// Theme order for cycling
export const THEME_ORDER = ['dark', 'light', 'highContrast', 'industrial'];

// Default theme
export const DEFAULT_THEME = 'dark';

// Storage key for persisting theme preference
export const THEME_STORAGE_KEY = 'waide_theme';

/**
 * Get theme by ID
 * @param {string} themeId - Theme identifier
 * @returns {object} Theme object
 */
export const getTheme = (themeId) => {
  return THEMES[themeId] || THEMES[DEFAULT_THEME];
};

/**
 * Get next theme in cycle
 * @param {string} currentThemeId - Current theme identifier
 * @returns {string} Next theme identifier
 */
export const getNextTheme = (currentThemeId) => {
  const currentIndex = THEME_ORDER.indexOf(currentThemeId);
  const nextIndex = (currentIndex + 1) % THEME_ORDER.length;
  return THEME_ORDER[nextIndex];
};

/**
 * Check if theme is dark variant
 * @param {string} themeId - Theme identifier
 * @returns {boolean}
 */
export const isDarkTheme = (themeId) => {
  return ['dark', 'highContrast', 'industrial'].includes(themeId);
};

/**
 * Get chart color palette for current theme
 * @param {string} themeId - Theme identifier
 * @returns {string[]} Array of chart colors
 */
export const getChartColors = (themeId) => {
  const theme = getTheme(themeId);
  return [
    theme.colors.primary.main,
    theme.colors.success.main,
    theme.colors.warning.main,
    theme.colors.info.main,
    theme.colors.secondary.main,
    theme.colors.error.main,
    theme.colors.primary.light,
    theme.colors.success.light,
  ];
};

/**
 * Status color mapping (consistent across themes)
 * Maps status strings to semantic color keys
 */
export const STATUS_COLOR_MAP = {
  // Success states
  success: 'success',
  completed: 'success',
  active: 'success',
  approved: 'success',
  available: 'success',
  received: 'success',
  shipped: 'success',
  delivered: 'success',

  // Warning states
  warning: 'warning',
  pending: 'warning',
  processing: 'warning',
  partial: 'warning',
  hold: 'warning',
  review: 'warning',

  // Error states
  error: 'error',
  failed: 'error',
  cancelled: 'error',
  rejected: 'error',
  damaged: 'error',
  expired: 'error',

  // Info states
  info: 'info',
  new: 'info',
  draft: 'info',
  scheduled: 'info',
};

/**
 * Get color for a status value
 * @param {string} status - Status string
 * @param {object} themeColors - Theme colors object
 * @returns {string} Color value
 */
export const getStatusColor = (status, themeColors) => {
  const normalizedStatus = status?.toLowerCase().replace(/[^a-z]/g, '');
  const colorKey = STATUS_COLOR_MAP[normalizedStatus] || 'info';
  return themeColors[colorKey]?.main || themeColors.text.secondary;
};

export default THEMES;
